import { FlexRow } from '@/components/common/Flex';
import ShareStatusToggle from '@/components/common/ShareStatusToggle';
import { Paragraph, Text } from '@/components/common/Typography';
import {
    ExternalFinancialEntityType,
    getDefaultName,
    getOwnershipTypeOptionValues,
} from '@common/financialEntities';
import { FinancialEntityOwnershipType } from '@common/graphql/generated';
import { FinancialEntityCombinedType } from '@common/react/financialEntities';
import { isSingleplayer, useCurrentUser } from '@common/react/user';
import React from 'react';
import { FlexCol } from '../common/Flex';
import { User03 } from '../common/icons';
import InputField from '../common/InputField';
import { OwnershipType } from './OwnershipType';

const SELECT_INPUT_WIDTH = 180;

// N.B. Keep in sync with mobile/components/accountLinking/AccountLinkingConfirmationInitialSubstep.tsx
const AccountLinkingConfirmationInitialSubstep = ({
    ownershipType,
    setOwnershipType,
    feType,
    feCombinedTypeOptions,
    feCombinedType,
    setFeCombinedType,
    isPrivate,
    setIsPrivate,
    feJointOwnerPartnershipId,
}: {
    ownershipType: FinancialEntityOwnershipType;
    setOwnershipType: (ownershipType: FinancialEntityOwnershipType) => void;
    feType: ExternalFinancialEntityType;
    feCombinedTypeOptions: {
        value: FinancialEntityCombinedType;
        label: string;
    }[];
    feCombinedType: FinancialEntityCombinedType;
    setFeCombinedType: (feCombinedType: FinancialEntityCombinedType) => void;
    isPrivate: boolean;
    setIsPrivate: (isPrivate: boolean) => void;
    feJointOwnerPartnershipId: string | null | undefined;
}) => {
    const currentUser = useCurrentUser();
    const { activePartnershipId } = currentUser;

    const ownershipTypeOptionValues = getOwnershipTypeOptionValues({
        feType,
        feJointOwnerPartnershipId,
        feIsMine: true,
        activePartnershipId,
    });
    const ownershipTypeOptions = ownershipTypeOptionValues.map((value) => {
        return {
            value,
            label: <OwnershipType value={value} />,
        };
    });

    return (
        <>
            <FlexCol bg="gray.50" rounded="md" px={6} py={4} gap={4} mb={4}>
                {!activePartnershipId ? (
                    // If user does not have a partner, hide ownership row
                    // because we don't support selecting Individual/Joint.
                    <></>
                ) : (
                    <FlexRow justifyContent="space-between" alignItems="center">
                        <Text>Ownership</Text>
                        {ownershipTypeOptionValues.length === 1 ? (
                            <Text color="textMuted" py={2}>
                                <OwnershipType value={ownershipType} />
                            </Text>
                        ) : (
                            <InputField
                                type="fancySelect"
                                fancySelectOptions={{
                                    isClearable: false,
                                }}
                                value={ownershipType}
                                onChange={({ value }) => {
                                    setOwnershipType(value);
                                }}
                                name="ownershipType"
                                options={[
                                    {
                                        label: 'WHO OWNS THIS ACCOUNT?',
                                        options: ownershipTypeOptions,
                                    },
                                ]}
                                bg="white"
                                w={SELECT_INPUT_WIDTH}
                            />
                        )}
                    </FlexRow>
                )}
                <FlexRow justifyContent="space-between" alignItems="center">
                    <Text>Account type</Text>
                    {feCombinedTypeOptions.length > 0 ? (
                        <InputField
                            type="fancySelect"
                            name="feCombinedType"
                            fancySelectOptions={{
                                isClearable: false,
                            }}
                            options={feCombinedTypeOptions}
                            value={feCombinedType}
                            onChange={({ value }) => {
                                setFeCombinedType(value);
                            }}
                            bg="white"
                            w={SELECT_INPUT_WIDTH}
                        />
                    ) : (
                        <Text color="textMuted" py={2}>
                            {getDefaultName(feType).replace(' Account', '')}
                        </Text>
                    )}
                </FlexRow>
            </FlexCol>
            {!!activePartnershipId && !isSingleplayer(currentUser) && (
                <FlexCol bg="gray.50" rounded="md" px={6} py={4} gap={4}>
                    <FlexRow justifyContent="space-between" alignItems="center">
                        <Text>Make this account private</Text>
                        <FlexRow>
                            <ShareStatusToggle
                                shareStatus={isPrivate ? 'PRIVATE' : 'SHARED'}
                                onChangeShareStatus={(shareStatus) => {
                                    setIsPrivate(shareStatus === 'PRIVATE');
                                }}
                            />
                        </FlexRow>
                    </FlexRow>
                    <Paragraph fontSize="sm" color="textMuted">
                        When you make this account private, your account (name,
                        balance, transactions and other details) will not be
                        visible to your partner. It will appear in the{' '}
                        <User03 size="sm" mx={1} />
                        What’s mine part of the dashboard.
                    </Paragraph>
                </FlexCol>
            )}
        </>
    );
};

export default AccountLinkingConfirmationInitialSubstep;
